import { Children, cloneElement, forwardRef, isValidElement, useCallback, useLayoutEffect, useRef, useState } from "react";
import { arrayOf, bool, func, node, object, string, number } from "prop-types";
import { isEqual } from "lodash";
import Quill, { Delta } from "quill";
import { Mention, MentionBlot } from "quill-mention";
import {
  MAX_WYSIWYG_LENGTH,
  WYSIWYG_FORMATS,
  WYSIWYG_FORMATS_WITH_MENTIONS,
  suggestMentionPeople,
  getQuillEditorToolPopupPosition,
  formatUrlOnPaste,
  autoFormatUrls,
} from "@utils";
import { NavigationDataKey } from "@tools/EstimateEditor/hooks/useNavigationKeybinds";
import { PopMenu, SearchableListContent, SearchableListItem } from "@components";
import { ClickAwayListener, Grid, InputAdornment, Link } from "@material-ui/core";
import { QuillToolbar } from "../QuillToolbar/QuillToolbar";
import classnames from "classnames";
import useStyle from "./QuillEditor.style";
import "quill/dist/quill.core.css";
import { useTheme } from "@material-ui/core/styles";

export const QuillEditor = forwardRef(({
  children,
  toolbarExtension,
  focused,
  value: defaultValue,
  displayValue,
  showDisplayValue,
  
  onFocus,
  onBlur,
  onTextChange,
  pinToolbarToContainer,
  editorMentionUsers,
  editorMentionSearchKeys,
  popupAnchor,
  placeholder,
  useBorder,
  useBorderUnfocused,
  useBorderHover,
  blurOnEnter,
  endAdornment,
  className,
  editorClassName,
  placeholderOnFocus,
  noEditorUpdatesOnChange,
  style,
  navigationKey,
  searchPhrases = [],
  maxContentLength = MAX_WYSIWYG_LENGTH,
}, ref ) => {
  const theme = useTheme();
  const classes = useStyle();
  
  const [ userListVisible, showUsers ] = useState(false);
  const [ filteredUsers, setFilteredUsers ] = useState([]);
  const [ loaded, setLoaded ] = useState(false);
  const [ anchor, setAnchor ] = useState(null);
  
  const containerRef = useRef(null);
  const toolbarRef = useRef(null);
  const navLinkHref = useRef(null);
  const onTextChangeRef = useRef(onTextChange);
  const defaultValueRef = useRef(defaultValue);
  const displayValueRef = useRef(displayValue);
  const selectionRef = useRef(0);
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  const lastWhitespaceRef = useRef(null);
  
  const filterMentionUsers = useCallback((searchTerm, renderList) => {
    let matchedPeople;
    if (searchTerm?.length === 0) {
      matchedPeople = suggestMentionPeople(editorMentionUsers);
    } else
      matchedPeople = suggestMentionPeople(editorMentionUsers, editorMentionSearchKeys, searchTerm);
    setFilteredUsers(matchedPeople);
    renderList?.(matchedPeople, searchTerm);
  }, [editorMentionUsers]);
  
  const handleBlur = (reason) => {
    if ( !ref.current || !focused )
      return;
    onBlur && onBlur(reason);
  }
  const handleContentEditableBlur = (e) => {
    if ( !e.relatedTarget ) {
      handleBlur("clickAway");
    }
  }
  
  const setDisplayOrDefaultValue = () => {
    let delta;
    if(showDisplayValue && displayValue) {
      delta = ref.current.clipboard.convert({ html: displayValue });
    } else {
      delta = ref.current.clipboard.convert({ html: defaultValue });
    }
    ref.current.setContents(delta, "silent");
  }
  
  const handleToolbarRef = useCallback((r) => {
    toolbarRef.current = r;
    setLoaded(Boolean(r));
  }, []);
  
  const addMention = (item) => async () => {
    ref.current?.getModule("mention")?.insertItem({
      denotationChar: "@",
      ...item
    }, false);
  }
  
  const applySearchPhraseMarkers = () => {
    if(focused || !ref.current)
      return;
    
    if(!searchPhrases.length) {
      ref.current.formatText(0, ref.current.getText().length, { background: null}, "api")
      return;
    }
    
    const reg = new RegExp(`${searchPhrases.map(p => `(${p.toLowerCase()})`).join("|")}`, "g");
    const res = [...ref.current.getText().toLowerCase().matchAll(reg)];
    for(const match of res) {
      ref.current.formatText(match.index, match[0].length, { background: theme.props.marker}, "api")
    }
  }
  
  useLayoutEffect(() => {
    onTextChangeRef.current = onTextChange;
    defaultValueRef.current = defaultValue;
    displayValueRef.current = displayValue;
    onFocusRef.current = onFocus;
    onBlurRef.current = onBlur;
  });
  
  useLayoutEffect(() => {
    if(!ref.current || noEditorUpdatesOnChange) return;
    if(!isEqual(defaultValue, ref.current.root.innerHTML)) {
      const selection = ref.current.getSelection();
      setDisplayOrDefaultValue();
      ref.current.setSelection(selection, "silent");
    }
  }, [defaultValue]);
  
  useLayoutEffect(() => {
    if(!displayValue || !ref.current) return;
    setDisplayOrDefaultValue()
  }, [ref, showDisplayValue]);
  
  useLayoutEffect(() => {
    applySearchPhraseMarkers();
  }, [searchPhrases]);
  
  useLayoutEffect(() => {
    const container = containerRef.current;
    const editorContainer = container.appendChild(
      container.ownerDocument.createElement("div"),
    );
    
    ref.current = new Quill(editorContainer, {
      theme: null,
      placeholder,
      formats: editorMentionUsers?.length ? WYSIWYG_FORMATS_WITH_MENTIONS : WYSIWYG_FORMATS,
      modules: {
        mention: editorMentionUsers?.length
          ? {
            source: filterMentionUsers,
            dataAttributes: ["value"],
            onOpen: () => showUsers(true),
            onClose: () => showUsers(false),
          }
          : undefined,
        history: {
          userOnly: true
        },
        toolbar: toolbarRef.current,
      }
    });
    
    ref.current.root.tabIndex = -1;
    
    if(blurOnEnter) {
      ref.current.keyboard.bindings["Enter"] = null;
      ref.current.keyboard.addBinding(
        "Enter",
        () => {
          onBlurRef.current?.("enter");
        }
      );
      ref.current.keyboard.addBinding(
        "Enter",
        { shiftKey: true },
        (range) => {
          ref.current.insertText(range.index, "\n");
        }
      );
    }
    else {
      ref.current.keyboard.addBinding(
        "Escape",
        () => {
          onBlurRef.current?.("escape");
          navLinkHref.current?.focus();
        }
      );
    }
    
    ref.current.root.addEventListener('paste', (ev) => {
      formatUrlOnPaste(ev, ref.current)
    }, true);
    
    ref.current.on(Quill.events.TEXT_CHANGE, (delta, _, source) => {
      if(source === "api")
        return;
      const contentLength = ref.current.getLength();
      if(contentLength > maxContentLength) {
        ref.current.deleteText(maxContentLength-1, contentLength);
        return;
      }
      
      if(delta.ops.length === 2 && typeof delta.ops[1].insert === "string" )
        autoFormatUrls(ref.current);
      
      // autoFormatUrls(ref.current);
      const deltaOps = ref.current.getContents()?.ops;

      const mentions = deltaOps
        ?.filter(ops => ops?.insert?.mention?.id)
        .map(ops => ({uuid: ops.insert.mention.id})) || [];

      onTextChangeRef.current?.(ref.current.root.innerHTML, mentions, contentLength, deltaOps);
    });

    ref.current.on(Quill.events.EDITOR_CHANGE, (eventName, rangeOrDelta, oldRangeOrDelta) => {
      if (isEqual(rangeOrDelta, oldRangeOrDelta)) return;
      if (eventName === Quill.events.SELECTION_CHANGE && rangeOrDelta && !pinToolbarToContainer) {
        setAnchor(getQuillEditorToolPopupPosition(ref.current, containerRef.current, rangeOrDelta))
      }
      if (eventName === Quill.events.SELECTION_CHANGE && !oldRangeOrDelta && rangeOrDelta) {
        // anchor.current = ref.current.selection.cursor.domNode;
        lastWhitespaceRef.current = rangeOrDelta.index;
        selectionRef.current = rangeOrDelta;
        onFocusRef.current?.();
      }
    });
    
    let areDifferent;
    if (defaultValueRef.current) {
      let html = defaultValueRef.current;
      if(showDisplayValue && displayValueRef.current)
        html = displayValueRef.current;
      
      const filter = () => {
        const ops = delta.ops
          .filter(o => (
            !o.insert?.mention || editorMentionUsers.find(u => u.uuid === o.insert.mention.id)
          ))
          
        if(focused)
          return ops.map(({ attributes, ...o }) => {
            if(attributes?.background !== undefined)
              delete attributes.background;
            
            if(attributes && Object.keys(attributes).length)
              return { ...o, attributes };
            return o;
          })
        
        return ops;
      }
      
      const delta = ref.current.clipboard.convert({ html })
      const filteredDelta = new Delta(filter());
      areDifferent = filteredDelta.ops.length !== delta.ops.length;
      ref.current.setContents(filteredDelta, areDifferent ? "user" : "silent");
      
      applySearchPhraseMarkers();
    }
    
    if(selectionRef.current && !areDifferent) {
      /** @note: "user" allows mention module insert right after focus on "@" */
      ref.current.setSelection(selectionRef.current, "user");
      selectionRef.current = null;
    }
    
    if(focused)
      ref.current.focus();
    
    return () => {
      setAnchor(null);
      ref.current = null;
      container.innerHTML = "";
    };
  }, [ref, loaded, editorMentionUsers]);
  
  const toolbarExtensionWithProps = Children.map(toolbarExtension, (child) => {
    if ( isValidElement(child) )
      return cloneElement(child, {
        ...child.props,
        onEditorBlur: () => handleBlur("clickAway")
      });
    return child;
  });
  
  const handleNavLinkPress = (e) => {
    const { key } = e;
    if(key === "Enter") {
      e.stopPropagation();
      ref.current?.root?.focus();
    }
  }
  
  const handleNavLinkKeyDown = (e) => {
    const { key, currentTarget } = e;
    if ( document.activeElement === currentTarget && key === "Escape" ) {
      e.stopPropagation();
      currentTarget.blur();
    }
  }
  
  return <ClickAwayListener mouseEvent="onMouseDown" onClickAway={() => handleBlur("clickAway")}>
    <Grid
      item container
      translate="no"
      wrap="nowrap"
      direction="column"
      alignItems="flex-start"
      className={classnames(
        classes.container,
        useBorder && useBorderHover && classes.borderedHover,
        "ql-root w-full relative", showDisplayValue && "ql-ellipsis",
        className,
      )}
      style={style}
    >
      { children }
      {
        navigationKey &&
        <Link
          href="#"
          ref={navLinkHref}
          name="quill-editor"
          className={classes.focusLink}
          {...{[NavigationDataKey]: navigationKey}}
          onKeyUp={handleNavLinkPress}
          onKeyDown={handleNavLinkKeyDown}
        >
          {""}
        </Link>
      }
      {
        useBorder &&
        <div className={classnames(
          classes.fieldset,
          useBorderUnfocused && classes.borderedUnfocused,
          focused && classes.borderedFocused
        )} />
      }
      <div
        ref={containerRef}
        onBlur={handleContentEditableBlur}
        className={classnames(
          classes.editor,
          placeholderOnFocus && classes.placeholderOnFocus,
          focused && "focused",
          editorClassName,
        )}
      />
      {
        endAdornment &&
        <InputAdornment position="end" onClick={() => handleBlur("endAdornment")}>
          { endAdornment }
        </InputAdornment>
      }
      {
        (pinToolbarToContainer ? popupAnchor : anchor) &&
        <PopMenu
          anchor={pinToolbarToContainer ? popupAnchor : anchor}
          show={focused}
          placement={pinToolbarToContainer ? "bottom" : "bottom-start"}
          pinToViewport noFlip
          offset={pinToolbarToContainer ? 8 : 40}
        >
          <QuillToolbar
            ref={handleToolbarRef}
            float visible
          >
            { toolbarExtensionWithProps }
          </QuillToolbar>
        </PopMenu>
      }
      {
        Boolean(editorMentionUsers) &&
        <SearchableListContent
          show={userListVisible}
          anchor={popupAnchor}
          placement="bottom"
          className={classes.mentionList}
          id="libraryElementSelector"
          onClickAway={() => showUsers(false)}
        >
          {
            filteredUsers.map(m => (
              <SearchableListItem
                key={m.id}
                onClick={addMention(m)}
              >
                {m.value}
              </SearchableListItem>
            ))
          }
        </SearchableListContent>
      }
    </Grid>
  </ClickAwayListener>;
});

QuillEditor.displayName = "QuillEditor";
QuillEditor.propTypes = {
  disabled: bool,
  popupAnchor: object,
  value: string,
  displayValue: string,
  showDisplayValue: bool,
  focused: bool,
  onFocus: func,
  onBlur: func,
  onTextChange: func.isRequired,
  useToolbar: bool,
  pinToolbarToContainer: bool,
  children: node,
  toolbarExtension: node,
  endAdornment: node,
  useBorder: bool,
  useBorderHover: bool,
  useBorderUnfocused: bool,
  placeholderOnFocus: bool,
  blurOnEnter: bool,
  placeholder: string,
  editorMentionUsers: arrayOf(object),
  editorMentionSearchKeys: arrayOf(string),
  searchPhrases: arrayOf(string),
  className: string,
  editorClassName: string,
  noEditorUpdatesOnChange: bool,
  style: object,
  navigationKey: string,
  maxContentLength: number,
}

Quill.register({ "blots/mention": MentionBlot, "modules/mention": Mention });
const icons = Quill.import("ui/icons");
icons.bold = null;
icons.italic = null;
icons.underline = null;
icons.code = null;
icons["code-block"] = null;
icons.blockquote = null;
icons.strike = null;
icons.underline = null;
icons.link = null;
icons.header = {
  1: null,
  2: null,
};
icons.list = {
  ordered: null,
  bullet: null,
};